import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage({ setUser }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    role: 'company_admin',
    plan: 'big_business'  // Always Big Business for 5-day trial
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        
        // For new registrations, show welcome guide
        if (!isLogin) {
          localStorage.setItem('show_welcome_guide', 'true');
          localStorage.setItem('new_company_name', formData.company_name);
        }
        
        setUser(data.user);
        toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
        navigate('/');
      } else {
        toast.error(data.detail || 'Authentication failed');
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="auth-page">
      <div className="auth-container">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle data-testid="auth-title">{isLogin ? 'Welcome Back' : 'Create Account'}</CardTitle>
            <CardDescription data-testid="auth-description">
              {isLogin ? 'Sign in to your account' : 'Register for a new account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="auth-form">
              {!isLogin && (
                <>
                  <div className="form-group">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      data-testid="company-name-input"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="phone-input"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="plan">Select Plan</Label>
                    <Select
                      value={formData.plan}
                      onValueChange={(value) => setFormData({ ...formData, plan: value })}
                    >
                      <SelectTrigger data-testid="plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter - $55/month (8 people)</SelectItem>
                        <SelectItem value="small_business">Small Business - $125/month (20 people)</SelectItem>
                        <SelectItem value="mid_size">Mid Size - $250/month (45 people)</SelectItem>
                        <SelectItem value="big_business">Big Business - $350/month (75 people)</SelectItem>
                        <SelectItem value="enterprise">Enterprise - Contact Sales (76+ people)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-gray-600 mt-1">
                      ✨ You'll get a <strong>5-day free trial</strong> with <strong>Big Business</strong> features regardless of your selection!
                    </p>
                  </div>

                  <div className="form-group">
                    <Label>Your Role</Label>
                    <Input
                      value="Company Admin"
                      disabled
                      className="bg-gray-100"
                    />
                    <p className="text-sm text-gray-500 mt-1">You'll be registered as the Company Admin</p>
                  </div>
                </>
              )}
              <div className="form-group">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
            <div className="auth-switch">
              <p>
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                <Button
                  variant="link"
                  onClick={() => setIsLogin(!isLogin)}
                  data-testid="toggle-auth-btn"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </Button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <style jsx>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem;
        }

        .auth-container {
          width: 100%;
          max-width: 450px;
        }

        .auth-card {
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .auth-switch {
          margin-top: 1.5rem;
          text-align: center;
        }

        .auth-switch p {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: #64748b;
        }

        /* ====================================
           MOBILE RESPONSIVE STYLES
           ==================================== */
        
        @media (max-width: 768px) {
          .auth-page {
            padding: 1rem;
          }

          .auth-container {
            max-width: 100%;
          }

          .auth-card {
            padding: 1.5rem !important;
          }

          .auth-form {
            gap: 1.25rem;
          }

          /* Touch-friendly inputs */
          input, select {
            font-size: 16px !important; /* Prevents zoom on iOS */
            min-height: 48px !important;
            padding: 0.75rem !important;
          }

          /* Touch-friendly buttons */
          button {
            min-height: 48px !important;
            padding: 0.875rem 1.5rem !important;
            font-size: 1rem !important;
          }

          /* Title */
          .card-title {
            font-size: 1.5rem !important;
          }

          /* Description */
          .card-description {
            font-size: 0.875rem !important;
          }

          /* Labels */
          label {
            font-size: 0.875rem !important;
            font-weight: 500 !important;
          }

          /* Form groups */
          .form-group {
            gap: 0.5rem;
          }

          /* Auth switch */
          .auth-switch {
            margin-top: 1rem;
          }

          .auth-switch p {
            font-size: 0.875rem;
            flex-wrap: wrap;
          }
        }

        @media (max-width: 375px) {
          .auth-page {
            padding: 0.5rem;
          }

          .auth-card {
            padding: 1rem !important;
          }

          .card-title {
            font-size: 1.25rem !important;
          }

          .auth-form {
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
